import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Heart, Instagram, Search, X, ExternalLink, TrendingUp, Users, Eye, ChevronDown, Star, BadgeCheck, Play } from 'lucide-react';
import { supabase } from '../lib/supabase';

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const YouTubeIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const SnapchatIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.166 2C9.036 2 7.07 3.686 6.326 5.956c-.199.6-.165 1.817-.148 2.535l-.017.003c-.27.044-.57.028-.88-.02-.277-.044-.54-.11-.77-.11-.43 0-.814.168-.99.5-.13.244-.13.524.02.784.31.544 1.05.946 2.09 1.134.014.006.025.01.038.016-.016.053-.033.107-.05.162-.2.65-.557 1.24-1.09 1.617-.43.302-.644.782-.547 1.252.08.393.38.72.81.876.246.089.53.128.842.114.215-.01.44-.04.672-.082.383.636 1.044 1.082 2.098 1.266.88.153 1.91.128 2.72.12h.008c.81.008 1.84.033 2.72-.12 1.054-.184 1.715-.63 2.098-1.266.232.042.457.072.672.082.312.014.596-.025.842-.114.43-.156.73-.483.81-.876.097-.47-.117-.95-.547-1.252-.533-.377-.89-.966-1.09-1.617a5.49 5.49 0 01-.05-.162c.013-.006.024-.01.038-.016 1.04-.188 1.78-.59 2.09-1.134.15-.26.15-.54.02-.784-.176-.332-.56-.5-.99-.5-.23 0-.493.066-.77.11-.31.048-.61.064-.88.02l-.017-.003c.017-.718.051-1.935-.148-2.535C16.93 3.686 14.964 2 11.834 2z"/>
  </svg>
);

interface Influencer {
  id: string;
  username: string;
  display_name: string | null;
  profile_image_url: string | null;
  followers_count: number;
  engagement_rate: number;
  category: string | null;
  bio: string | null;
  is_exclusive: boolean;
  avg_likes: number | null;
  avg_comments: number | null;
  avg_views?: number | null;
  hasTiktok?: boolean;
  tiktok?: {
    followers_count: number;
    avg_views: number;
    engagement_rate: number | null;
    tiktok_username: string;
  };
  hasYoutube?: boolean;
  youtube?: {
    subscribers_count: number;
    avg_views: number;
    engagement_rate: number | null;
    youtube_handle: string;
    channel_name: string | null;
  };
  hasSnapchat?: boolean;
  snapchat?: {
    username: string;
    name: string | null;
    subscriber_count: number;
    avg_spotlight_views?: number;
    spotlights_count?: number;
    profile_picture_url: string | null;
    snapcode_url: string | null;
    bio: string | null;
  };
}

interface Post {
  id: string;
  post_url: string | null;
  image_url: string | null;
  caption: string | null;
  likes_count: number;
  comments_count: number;
  post_type: string | null;
  video_view_count: number | null;
}

interface TikTokProfile {
  tiktok_username: string;
  followers_count: number;
  engagement_rate: number | null;
  avg_views: number;
  avg_likes: number;
}

interface TikTokPost {
  id: string;
  video_url: string | null;
  thumbnail_url: string | null;
  likes_count: number;
  views_count: number;
  description: string | null;
}

interface YouTubeProfile {
  youtube_handle: string;
  channel_name: string | null;
  subscribers_count: number;
  avg_views: number;
  avg_likes: number;
  engagement_rate: number | null;
  scraped_at: string;
}

interface YouTubeVideo {
  id: string;
  video_url: string | null;
  thumbnail_url: string | null;
  title: string | null;
  view_count: number;
  likes_count: number;
}

interface SnapchatProfile {
  username: string;
  display_name: string | null;
  name: string | null;
  subscriber_count: number;
  profile_picture_url: string | null;
  snapcode_url: string | null;
  bio: string | null;
  location: string | null;
  category: string | null;
  website_url: string | null;
  profile_url: string | null;
  scraped_at: string;
}

interface SnapchatSnap {
  id: string;
  snap_url: string | null;
  thumbnail_url: string | null;
  upload_timestamp: string | null;
  media_type: string;
  source: string;
}

interface SnapchatStory {
  id: string;
  story_title: string | null;
  thumbnail_url: string | null;
  snaps: Array<{ mediaUrl?: string; previewUrl?: string; timestamp?: string }>;
}

interface SnapchatSpotlight {
  id: string;
  title: string | null;
  description: string | null;
  views: number;
  thumbnail_url: string | null;
  hashtags: string[];
  snaps: Array<{ mediaUrl?: string; previewUrl?: string; timestamp?: string }>;
}

interface SelectionItem {
  influencer: Influencer;
  note: string;
}

interface ClientData {
  id: string;
  brand_name: string;
  public_slug: string;
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return String(n);
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

function proxyImg(url: string | null): string {
  if (!url) return '';
  // Only proxy external Instagram/CDN URLs
  if (url.startsWith('http') && !url.includes('ui-avatars.com')) {
    return `${SUPABASE_URL}/functions/v1/proxy-image?url=${encodeURIComponent(url)}`;
  }
  return url;
}

function getVisitorToken(): string {
  const key = 'roster_visitor_token';
  let token = localStorage.getItem(key);
  if (!token) {
    token = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(key, token);
  }
  return token;
}

function getLikedKey(slug: string): string {
  return `roster_liked_${slug}`;
}

export default function PublicRosterView() {
  const { slug } = useParams<{ slug: string }>();
  const [client, setClient] = useState<ClientData | null>(null);
  const [selections, setSelections] = useState<SelectionItem[]>([]);
  const [roster, setRoster] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [search, setSearch] = useState('');

  const [activeProfile, setActiveProfile] = useState<Influencer | null>(null);
  const [profilePosts, setProfilePosts] = useState<Post[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [tiktokProfile, setTiktokProfile] = useState<TikTokProfile | null>(null);
  const [tiktokVideos, setTiktokVideos] = useState<TikTokPost[]>([]);
  const [youtubeProfile, setYoutubeProfile] = useState<YouTubeProfile | null>(null);
  const [youtubeVideos, setYoutubeVideos] = useState<YouTubeVideo[]>([]);
  const [snapchatProfile, setSnapchatProfile] = useState<SnapchatProfile | null>(null);
  const [snapchatSnaps, setSnapchatSnaps] = useState<SnapchatSnap[]>([]);
  const [snapchatStories, setSnapchatStories] = useState<SnapchatStory[]>([]);
  const [snapchatSpotlights, setSnapchatSpotlights] = useState<SnapchatSpotlight[]>([]);

  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const visitorToken = useRef(getVisitorToken());
  const viewTracked = useRef(false);

  useEffect(() => {
    if (!slug) return;
    // Restore liked from localStorage
    try {
      const stored = localStorage.getItem(getLikedKey(slug));
      if (stored) setLikedIds(new Set(JSON.parse(stored)));
    } catch { /* ignore */ }
    loadPage();
  }, [slug]);

  const loadPage = async () => {
    if (!slug) return;
    setLoading(true);
    try {
      // Load client
      const { data: clientData } = await supabase
        .from('clients')
        .select('id, brand_name, public_slug')
        .eq('public_slug', slug)
        .maybeSingle();

      if (!clientData) { setNotFound(true); setLoading(false); return; }
      setClient(clientData);

      // Track page view (once)
      if (!viewTracked.current) {
        viewTracked.current = true;
        supabase.from('clients').update({ page_views: (clientData as any).page_views + 1 }).eq('id', clientData.id).then(() => {});
      }

      // Load selections
      const { data: selsData } = await supabase
        .from('roster_selections')
        .select('influencer_id, note, position')
        .eq('client_id', clientData.id)
        .order('position');

      // Load full roster (organization_influencers linked to influencers)
      const { data: orgInfs } = await supabase
        .from('organization_influencers')
        .select('influencer_id')
        .limit(500);

      const orgIds = (orgInfs || []).map(o => o.influencer_id);

      let influencersData: Influencer[] = [];
      if (orgIds.length > 0) {
        // batch fetch (max 100 at a time to avoid query limit)
        const batches: Influencer[][] = [];
        for (let i = 0; i < orgIds.length; i += 100) {
          const batch = orgIds.slice(i, i + 100);
          const { data } = await supabase
            .from('influencers')
            .select('id, username, display_name, profile_image_url, followers_count, engagement_rate, category, bio, is_exclusive, avg_likes, avg_comments')
            .in('id', batch)
            .order('followers_count', { ascending: false });
          if (data) batches.push(data as Influencer[]);
        }
        influencersData = batches.flat();
      }

      // Fetch TikTok + YouTube profiles and embed stats into influencer objects
      if (influencersData.length > 0) {
        const ids = influencersData.map(i => i.id);
        const [{ data: ttProfiles }, { data: ytProfiles }, { data: snapProfiles }, { data: snapSplights }] = await Promise.all([
          supabase.from('tiktok_profiles').select('influencer_id, tiktok_username, followers_count, avg_views, engagement_rate').in('influencer_id', ids),
          supabase.from('youtube_profiles').select('influencer_id, youtube_handle, channel_name, subscribers_count, avg_views, engagement_rate').in('influencer_id', ids),
          supabase.from('snapchat_profiles').select('influencer_id, username, name, subscriber_count, profile_picture_url, snapcode_url, bio').in('influencer_id', ids),
          supabase.from('snapchat_spotlights').select('influencer_id, views').in('influencer_id', ids),
        ]);
        const ttMap = new Map((ttProfiles || []).map(t => [t.influencer_id, t]));
        const ytMap = new Map((ytProfiles || []).map(y => [y.influencer_id, y]));
        const snapMap = new Map((snapProfiles || []).map(s => [s.influencer_id, s]));
        // Compute avg spotlight views per influencer
        const spotlightsByInfluencer = new Map<string, number[]>();
        for (const sp of (snapSplights || [])) {
          if (!spotlightsByInfluencer.has(sp.influencer_id)) spotlightsByInfluencer.set(sp.influencer_id, []);
          spotlightsByInfluencer.get(sp.influencer_id)!.push(sp.views || 0);
        }
        influencersData = influencersData.map(i => {
          const tt = ttMap.get(i.id);
          const yt = ytMap.get(i.id);
          const snap = snapMap.get(i.id);
          const spViews = spotlightsByInfluencer.get(i.id) || [];
          const avgSpViews = spViews.length > 0 ? Math.round(spViews.reduce((a, b) => a + b, 0) / spViews.length) : 0;
          return {
            ...i,
            hasTiktok: !!tt,
            tiktok: tt ? { followers_count: tt.followers_count, avg_views: tt.avg_views, engagement_rate: tt.engagement_rate, tiktok_username: tt.tiktok_username } : undefined,
            hasYoutube: !!yt,
            youtube: yt ? { subscribers_count: yt.subscribers_count, avg_views: yt.avg_views, engagement_rate: yt.engagement_rate, youtube_handle: yt.youtube_handle, channel_name: yt.channel_name } : undefined,
            hasSnapchat: !!snap,
            snapchat: snap ? { username: snap.username, name: snap.name, subscriber_count: snap.subscriber_count, profile_picture_url: snap.profile_picture_url, snapcode_url: snap.snapcode_url, bio: snap.bio, avg_spotlight_views: avgSpViews, spotlights_count: spViews.length } : undefined,
          };
        });
      }

      const infMap = new Map(influencersData.map(i => [i.id, i]));

      const selItems: SelectionItem[] = (selsData || [])
        .map(s => ({ influencer: infMap.get(s.influencer_id)!, note: s.note || '' }))
        .filter(s => s.influencer);

      setSelections(selItems);
      setRoster(influencersData);
    } finally {
      setLoading(false);
    }
  };

  const openProfile = async (inf: Influencer) => {
    setActiveProfile(inf);
    setTiktokProfile(null);
    setTiktokVideos([]);
    setYoutubeProfile(null);
    setYoutubeVideos([]);
    setSnapchatProfile(null);
    setSnapchatSnaps([]);
    setSnapchatStories([]);
    setSnapchatSpotlights([]);
    setLoadingPosts(true);

    const [postsRes, ttProfileRes, ttVideosRes, ytProfileRes, ytVideosRes, snapProfileRes, snapStoriesRes, snapSpotlightsRes] = await Promise.all([
      supabase
        .from('posts')
        .select('id, post_url, image_url, caption, likes_count, comments_count, post_type, video_view_count')
        .eq('influencer_id', inf.id)
        .order('likes_count', { ascending: false })
        .limit(9),
      supabase
        .from('tiktok_profiles')
        .select('tiktok_username, followers_count, engagement_rate, avg_views, avg_likes')
        .eq('influencer_id', inf.id)
        .maybeSingle(),
      supabase
        .from('tiktok_posts')
        .select('id, video_url, thumbnail_url, likes_count, views_count, description')
        .eq('influencer_id', inf.id)
        .order('views_count', { ascending: false })
        .limit(15),
      supabase
        .from('youtube_profiles')
        .select('youtube_handle, channel_name, subscribers_count, avg_views, avg_likes, engagement_rate, scraped_at')
        .eq('influencer_id', inf.id)
        .maybeSingle(),
      supabase
        .from('youtube_videos')
        .select('id, video_url, thumbnail_url, title, view_count, likes_count')
        .eq('influencer_id', inf.id)
        .order('view_count', { ascending: false })
        .limit(15),
      supabase
        .from('snapchat_profiles')
        .select('username, display_name, name, subscriber_count, profile_picture_url, snapcode_url, bio, location, category, website_url, profile_url, scraped_at')
        .eq('influencer_id', inf.id)
        .maybeSingle(),
      supabase
        .from('snapchat_stories')
        .select('id, story_title, thumbnail_url, snaps')
        .eq('influencer_id', inf.id)
        .order('created_at', { ascending: false })
        .limit(20),
      supabase
        .from('snapchat_spotlights')
        .select('id, title, description, views, thumbnail_url, hashtags, snaps')
        .eq('influencer_id', inf.id)
        .order('views', { ascending: false }),
    ]);

    const posts = postsRes.data || [];
    setProfilePosts(posts);
    if (ttProfileRes.data) setTiktokProfile(ttProfileRes.data);
    if (ttVideosRes.data) setTiktokVideos(ttVideosRes.data);
    if (ytProfileRes.data) setYoutubeProfile(ytProfileRes.data);
    if (ytVideosRes.data) setYoutubeVideos(ytVideosRes.data);
    if (snapProfileRes.data) setSnapchatProfile(snapProfileRes.data);
    if (snapStoriesRes.data) setSnapchatStories(snapStoriesRes.data);
    if (snapSpotlightsRes.data) setSnapchatSpotlights(snapSpotlightsRes.data);

    const viewCounts = posts.map(p => p.video_view_count).filter((v): v is number => v != null && v > 0);
    const avgViews = viewCounts.length > 0
      ? Math.round(viewCounts.reduce((s, v) => s + v, 0) / viewCounts.length)
      : null;
    setActiveProfile({ ...inf, avg_views: avgViews });
    setLoadingPosts(false);
  };

  const toggleLike = async (inf: Influencer) => {
    if (!client) return;
    const newLiked = new Set(likedIds);
    if (likedIds.has(inf.id)) {
      newLiked.delete(inf.id);
      await supabase
        .from('roster_likes')
        .delete()
        .eq('client_id', client.id)
        .eq('influencer_id', inf.id)
        .eq('visitor_token', visitorToken.current);
    } else {
      newLiked.add(inf.id);
      const { error } = await supabase.from('roster_likes').insert({
        client_id: client.id,
        influencer_id: inf.id,
        client_slug: slug!,
        visitor_token: visitorToken.current,
      });
      if (error) console.error('roster_likes insert error:', error);
    }
    setLikedIds(newLiked);
    localStorage.setItem(getLikedKey(slug!), JSON.stringify([...newLiked]));
  };

  const filteredRoster = roster.filter(inf => {
    if (search) {
      const q = search.toLowerCase();
      return (inf.username || '').toLowerCase().includes(q) || (inf.display_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !client) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-center px-4">
        <div>
          <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-white/30" />
          </div>
          <p className="text-white/60 text-lg">Page introuvable</p>
          <p className="text-white/30 text-sm mt-1">Ce lien n'est pas valide ou a expiré.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">

      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
              <span className="text-xs font-bold text-white">{client.brand_name[0]}</span>
            </div>
            <span className="font-semibold text-sm text-white/90">Roster pour {client.brand_name}</span>
          </div>
          <div className="flex items-center gap-2">
            {likedIds.size > 0 && (
              <span className="flex items-center gap-1.5 text-xs text-rose-400 font-medium bg-rose-500/10 px-2.5 py-1 rounded-full">
                <Heart className="w-3 h-3 fill-rose-400" />
                {likedIds.size} profil{likedIds.size > 1 ? 's' : ''} aimé{likedIds.size > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-20">

        {/* Hero */}
        <div className="pt-12 pb-10">
          <p className="text-white/40 text-sm uppercase tracking-widest font-medium mb-2">Présentation exclusive</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">
            Notre sélection de<br />créateurs pour vous
          </h1>
          <p className="text-white/50 text-base max-w-lg">
            Explorez les profils que nous avons sélectionnés spécialement pour {client.brand_name}, ainsi que l'ensemble de notre roster.
          </p>
        </div>

        {/* Selection section */}
        {selections.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Star className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Notre sélection pour vous</h2>
                <p className="text-xs text-white/40">{selections.length} profil{selections.length > 1 ? 's' : ''} choisi{selections.length > 1 ? 's' : ''} spécialement</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {selections.map(({ influencer: inf, note }) => (
                <SelectionCard
                  key={inf.id}
                  inf={inf}
                  note={note}
                  liked={likedIds.has(inf.id)}
                  onLike={() => toggleLike(inf)}
                  onOpen={() => openProfile(inf)}
                  highlight
                />
              ))}
            </div>
          </section>
        )}

        {/* Full roster section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Users className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Tout notre roster</h2>
              <p className="text-xs text-white/40">{roster.length} créateur{roster.length > 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full pl-9 pr-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-sm text-white placeholder-white/30 focus:border-white/20 focus:ring-0 transition-colors"
              />
            </div>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="text-xs text-white/30 hover:text-white/60 transition-colors"
              >
                Réinitialiser
              </button>
            )}
          </div>

          {filteredRoster.length === 0 ? (
            <div className="text-center py-16 text-white/30">Aucun résultat</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredRoster.map(inf => (
                <RosterCard
                  key={inf.id}
                  inf={inf}
                  liked={likedIds.has(inf.id)}
                  onLike={() => toggleLike(inf)}
                  onOpen={() => openProfile(inf)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Profile modal */}
      {activeProfile && (
        <ProfileModal
          inf={activeProfile}
          posts={profilePosts}
          loadingPosts={loadingPosts}
          liked={likedIds.has(activeProfile.id)}
          onLike={() => toggleLike(activeProfile)}
          onClose={() => setActiveProfile(null)}
          tiktokProfile={tiktokProfile}
          tiktokVideos={tiktokVideos}
          youtubeProfile={youtubeProfile}
          youtubeVideos={youtubeVideos}
          snapchatProfile={snapchatProfile}
          snapchatSnaps={snapchatSnaps}
          snapchatStories={snapchatStories}
          snapchatSpotlights={snapchatSpotlights}
        />
      )}
    </div>
  );
}

// ── Selection card (highlighted) ──────────────────────────────────────────────

function SelectionCard({ inf, note, liked, onLike, onOpen }: {
  inf: Influencer; note: string; liked: boolean; onLike: () => void; onOpen: () => void; highlight?: boolean;
}) {
  return (
    <div
      className="relative rounded-2xl border border-white/[0.10] bg-white/[0.05] hover:border-white/20 hover:bg-white/[0.07] transition-all cursor-pointer group p-4 flex flex-col gap-3"
      onClick={onOpen}
    >
      {/* Badge + like */}
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded-full">
          <Star className="w-2.5 h-2.5" /> Sélectionné
        </span>
        <button
          onClick={e => { e.stopPropagation(); onLike(); }}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
            liked ? 'bg-rose-500/20 text-rose-400' : 'bg-white/10 text-white/40 hover:bg-white/20 hover:text-white/70'
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-rose-400' : ''}`} />
        </button>
      </div>

      {/* Avatar + info */}
      <div className="flex items-center gap-3">
        <div className="relative flex-shrink-0">
          <img
            src={proxyImg(inf.profile_image_url) || `https://ui-avatars.com/api/?name=${inf.username}&background=1e293b&color=94a3b8&size=80`}
            alt={inf.username}
            className="w-14 h-14 rounded-full object-cover ring-2 ring-white/10 group-hover:ring-white/20 transition-all"
          />
          {inf.is_exclusive && (
            <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center border-2 border-[#0a0a0a]">
              <BadgeCheck className="w-3 h-3 text-white" />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="font-bold text-white text-sm truncate">@{inf.username}</p>
          <p className="text-white/50 text-xs">{formatNumber(inf.followers_count)} abonnés</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between pt-1 border-t border-white/[0.06]">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs">
            <TrendingUp className="w-3 h-3 text-emerald-400" />
            <span className="text-emerald-400 font-semibold">{inf.engagement_rate?.toFixed(1)}%</span>
          </div>
          {inf.avg_views != null && (
            <div className="flex items-center gap-1 text-xs text-white/40">
              <Eye className="w-3 h-3" />
              {formatNumber(inf.avg_views)} vues
            </div>
          )}
        </div>
        <div className="flex items-center rounded-full border border-white/[0.10] bg-white/[0.06] overflow-hidden">
          <div className="flex items-center justify-center w-6 h-6 bg-gradient-to-br from-amber-400 via-rose-500 to-purple-600 text-white">
            <Instagram className="w-3 h-3" />
          </div>
          {inf.hasTiktok && (
            <div className="flex items-center justify-center w-6 h-6 text-[#00F2EA]">
              <TikTokIcon className="w-3 h-3" />
            </div>
          )}
          {inf.hasYoutube && (
            <div className="flex items-center justify-center w-6 h-6 text-[#FF0000]">
              <YouTubeIcon className="w-3 h-3" />
            </div>
          )}
          {inf.hasSnapchat && (
            <div className="flex items-center justify-center w-6 h-6 text-[#FFFC00]">
              <SnapchatIcon className="w-3 h-3" />
            </div>
          )}
        </div>
      </div>

      {note && (
        <p className="text-[11px] text-white/40 leading-relaxed italic border-t border-white/[0.06] pt-2">
          "{note}"
        </p>
      )}
    </div>
  );
}

// ── Roster card (compact) ─────────────────────────────────────────────────────

function RosterCard({ inf, liked, onLike, onOpen }: {
  inf: Influencer; liked: boolean; onLike: () => void; onOpen: () => void;
}) {
  const [platform, setPlatform] = useState<'instagram' | 'tiktok' | 'youtube' | 'snapchat'>('instagram');
  const isTiktok = platform === 'tiktok' && !!inf.tiktok;
  const isYoutube = platform === 'youtube' && !!inf.youtube;
  const isSnapchat = platform === 'snapchat' && !!inf.snapchat;

  const followers = isTiktok ? inf.tiktok!.followers_count : isYoutube ? inf.youtube!.subscribers_count : isSnapchat ? inf.snapchat!.subscriber_count : inf.followers_count;
  const engagement = isTiktok ? inf.tiktok!.engagement_rate : isYoutube ? inf.youtube!.engagement_rate : isSnapchat ? null : inf.engagement_rate;
  const avgViews = isTiktok ? inf.tiktok!.avg_views : isYoutube ? inf.youtube!.avg_views : isSnapchat ? (inf.snapchat!.avg_spotlight_views || null) : null;
  const snapSpotlightsCount = isSnapchat ? (inf.snapchat!.spotlights_count || 0) : 0;

  return (
    <div
      className="relative rounded-2xl border border-white/[0.08] bg-white/[0.04] hover:border-white/[0.15] hover:bg-white/[0.06] transition-all cursor-pointer group p-3 flex items-center gap-3"
      onClick={onOpen}
    >
      <div className="relative flex-shrink-0">
        <img
          src={proxyImg(inf.profile_image_url) || `https://ui-avatars.com/api/?name=${inf.username}&background=1e293b&color=94a3b8&size=80`}
          alt={inf.username}
          className="w-12 h-12 rounded-full object-cover ring-2 ring-white/10 group-hover:ring-white/20 transition-all"
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-bold text-white text-xs truncate">@{inf.username}</p>
        <p className="text-white/50 text-[11px]">{formatNumber(followers)} abonnés</p>
        <div className="flex items-center gap-2 mt-1">
          {engagement != null && (
            <span className="text-[11px] text-emerald-400 font-semibold">{engagement.toFixed(1)}%</span>
          )}
          {(isTiktok || isYoutube) && avgViews != null && (
            <span className="text-[11px] text-white/40 flex items-center gap-0.5">
              <Play className="w-2.5 h-2.5" />{formatNumber(avgViews)} moy.
            </span>
          )}
          {isSnapchat && avgViews != null && avgViews > 0 && (
            <span className="text-[11px] text-[#FFFC00]/70 flex items-center gap-0.5">
              <Eye className="w-2.5 h-2.5" />{formatNumber(avgViews)} vues
            </span>
          )}
          {isSnapchat && snapSpotlightsCount > 0 && (
            <span className="text-[11px] text-white/40">{snapSpotlightsCount} spotlights</span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <div
            className="flex items-center rounded-full border border-white/[0.10] bg-white/[0.06] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={e => { e.stopPropagation(); setPlatform('instagram'); }}
              className={`flex items-center justify-center w-6 h-6 transition-all ${platform === 'instagram' ? 'bg-gradient-to-br from-amber-400 via-rose-500 to-purple-600 text-white' : 'text-white/30 hover:text-white/60'}`}
              title="Instagram"
            >
              <Instagram className="w-3 h-3" />
            </button>
            {inf.hasTiktok && (
              <button
                onClick={e => { e.stopPropagation(); setPlatform('tiktok'); }}
                className={`flex items-center justify-center w-6 h-6 transition-all ${platform === 'tiktok' ? 'bg-[#00F2EA]/20 text-[#00F2EA]' : 'text-white/30 hover:text-white/60'}`}
                title="TikTok"
              >
                <TikTokIcon className="w-3 h-3" />
              </button>
            )}
            {inf.hasYoutube && (
              <button
                onClick={e => { e.stopPropagation(); setPlatform('youtube'); }}
                className={`flex items-center justify-center w-6 h-6 transition-all ${platform === 'youtube' ? 'bg-[#FF0000]/20 text-[#FF0000]' : 'text-white/30 hover:text-white/60'}`}
                title="YouTube"
              >
                <YouTubeIcon className="w-3 h-3" />
              </button>
            )}
            {inf.hasSnapchat && (
              <button
                onClick={e => { e.stopPropagation(); setPlatform('snapchat'); }}
                className={`flex items-center justify-center w-6 h-6 transition-all ${platform === 'snapchat' ? 'bg-[#FFFC00]/20 text-[#FFFC00]' : 'text-white/30 hover:text-white/60'}`}
                title="Snapchat"
              >
                <SnapchatIcon className="w-3 h-3" />
              </button>
            )}
          </div>
        <button
          onClick={e => { e.stopPropagation(); onLike(); }}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
            liked ? 'bg-rose-500/20 text-rose-400' : 'bg-white/[0.08] text-white/30 hover:text-rose-400'
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-rose-400' : ''}`} />
        </button>
      </div>
    </div>
  );
}

// ── Profile modal ─────────────────────────────────────────────────────────────

interface BrandCollab {
  brand: string;
  count: number;
  latestDate: string | null;
  types: string[];
}

function getCollabTypeLabel(type: string): string {
  const map: Record<string, string> = {
    collaboration_entrante: 'Collab entrante',
    collaboration_sortante: 'Collab sortante',
    casting_interne: 'Casting interne',
    casting_externe: 'Casting externe',
  };
  return map[type] || type;
}

// Stopwords to strip when trying to find the real brand name in a composed string
const BRAND_STOPWORDS = ['fame', 'by', 'de', 'x', 'pour', 'avec', 'and', '&', 'the', 'le', 'la', 'les'];

// Extract the most likely "real" brand keyword from a composed name like "Fame - Rabanne"
function extractBrandKeyword(brand: string): string {
  // Split on separators: " - ", " x ", " & ", " / "
  const parts = brand.split(/\s*[-–/×x]\s*|\s+x\s+|\s+&\s+/i)
    .map(p => p.trim())
    .filter(p => p.length > 0);
  if (parts.length > 1) {
    // Return the part that is NOT a stopword and is longest
    const meaningful = parts.filter(p => !BRAND_STOPWORDS.includes(p.toLowerCase()));
    if (meaningful.length > 0) {
      return meaningful.sort((a, b) => b.length - a.length)[0];
    }
  }
  return brand;
}

function brandToDomain(brand: string): string | null {
  const keyword = extractBrandKeyword(brand);
  const normalized = keyword.toLowerCase().trim()
    .replace(/\s+/g, '')
    .replace(/[''']/g, '')
    .replace(/é/g, 'e').replace(/è/g, 'e').replace(/ê/g, 'e')
    .replace(/à/g, 'a').replace(/â/g, 'a')
    .replace(/ô/g, 'o').replace(/û/g, 'u')
    .replace(/ç/g, 'c')
    .replace(/&/g, 'and');

  const known: Record<string, string> = {
    // Fashion / Luxury
    rabanne: 'rabanne.com', paco: 'rabanne.com',
    dior: 'dior.com', chanel: 'chanel.com', gucci: 'gucci.com',
    hermes: 'hermes.com', cartier: 'cartier.com', ysl: 'ysl.com',
    saintlaurent: 'ysl.com', louisvuitton: 'louisvuitton.com', lvmh: 'lvmh.com',
    balenciaga: 'balenciaga.com', givenchy: 'givenchy.com', loewe: 'loewe.com',
    valentino: 'valentino.com', versace: 'versace.com', prada: 'prada.com',
    burberry: 'burberry.com', fendi: 'fendi.com', bottega: 'bottegaveneta.com',
    celine: 'celine.com', jacquemus: 'jacquemus.com', ami: 'ami-paris.com',
    isabel: 'isabelmarant.com', sandro: 'sandro-paris.com', maje: 'maje.com',
    // Sports / Lifestyle
    nike: 'nike.com', adidas: 'adidas.com', puma: 'puma.com',
    newbalance: 'newbalance.com', reebok: 'reebok.com', asics: 'asics.com',
    gymshark: 'gymshark.com', decathlon: 'decathlon.fr', lacoste: 'lacoste.com',
    // Fast fashion
    zara: 'zara.com', hm: 'hm.com', shein: 'shein.com', asos: 'asos.com',
    zalando: 'zalando.fr', primark: 'primark.com', uniqlo: 'uniqlo.com',
    mango: 'mango.com', boohoo: 'boohoo.com', revolve: 'revolve.com',
    // Beauty
    sephora: 'sephora.fr', loreal: 'loreal.fr', maybelline: 'maybelline.com',
    lancome: 'lancome.fr', nyx: 'nyxcosmetics.fr', mac: 'maccosmetics.com',
    makeup: 'makeupforever.com', makeupforever: 'makeupforever.com',
    urbandecay: 'urbandecay.com', benefit: 'benefitcosmetics.com',
    tarte: 'tartecosmetics.com', morphe: 'morphe.com', colourpop: 'colourpop.com',
    nuxe: 'nuxe.com', caudalie: 'caudalie.com', avene: 'avene.com',
    larocheposay: 'laroche-posay.fr', cerave: 'cerave.fr', vichy: 'vichy.fr',
    biotherm: 'biotherm.fr', neutrogena: 'neutrogena.fr', garnier: 'garnier.fr',
    loccitane: 'loccitane.com',
    // Fragrance
    adopt: 'adoptparfums.com', adoptparfums: 'adoptparfums.com',
    // Tech / Apps
    apple: 'apple.com', samsung: 'samsung.com', google: 'google.com',
    spotify: 'spotify.com', deezer: 'deezer.com', netflix: 'netflix.com',
    amazon: 'amazon.fr', uber: 'uber.com', deliveroo: 'deliveroo.fr',
    ubereats: 'ubereats.com', paypal: 'paypal.com', klarna: 'klarna.com',
    // Food / Lifestyle
    fruggies: 'fruggies.fr',
    booking: 'booking.com', airbnb: 'airbnb.fr',
  };

  if (known[normalized]) return known[normalized];
  // Generic fallback: use the extracted keyword as domain
  return `${normalized}.com`;
}

// Google favicon returns a generic globe SVG (807 bytes) when no favicon exists.
// We detect this by checking image natural width after load — the globe is always 16px.
function BrandLogo({ brand, size = 32 }: { brand: string; size?: number }) {
  // null = loading, true = found, false = not found
  const [logoFound, setLogoFound] = useState<boolean | null>(null);
  const domain = brandToDomain(brand);
  const faviconUrl = domain
    ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64`
    : null;

  const initials = brand.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
  const bgColors = [
    'bg-blue-900/70 text-blue-300',
    'bg-emerald-900/70 text-emerald-300',
    'bg-rose-900/70 text-rose-300',
    'bg-amber-900/70 text-amber-300',
    'bg-cyan-900/70 text-cyan-300',
    'bg-slate-800 text-slate-300',
  ];
  const bgColor = bgColors[brand.charCodeAt(0) % bgColors.length];
  const sz = `${size}px`;

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Google returns a 16x16 globe when favicon doesn't exist
    const img = e.currentTarget;
    if (img.naturalWidth <= 16 && img.naturalHeight <= 16) {
      setLogoFound(false);
    } else {
      setLogoFound(true);
    }
  };

  if (logoFound === false || !faviconUrl) {
    return (
      <div
        className={`rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${bgColor}`}
        style={{ width: sz, height: sz }}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      className="rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden relative"
      style={{ width: sz, height: sz }}
    >
      {/* Shown while loading or when found */}
      <img
        src={faviconUrl}
        alt={brand}
        style={{ width: Math.round(size * 0.72), height: Math.round(size * 0.72), objectFit: 'contain', opacity: logoFound ? 1 : 0 }}
        onLoad={handleLoad}
        onError={() => setLogoFound(false)}
      />
      {/* Initials shown during loading state */}
      {logoFound === null && (
        <div className={`absolute inset-0 rounded-lg flex items-center justify-center text-[10px] font-bold ${bgColor}`}>
          {initials}
        </div>
      )}
    </div>
  );

}

function ProfileModal({ inf, posts, loadingPosts, liked, onLike, onClose, tiktokProfile, tiktokVideos, youtubeProfile, youtubeVideos, snapchatProfile, snapchatSnaps, snapchatStories, snapchatSpotlights }: {
  inf: Influencer; posts: Post[]; loadingPosts: boolean; liked: boolean; onLike: () => void; onClose: () => void;
  tiktokProfile: TikTokProfile | null; tiktokVideos: TikTokPost[];
  youtubeProfile: YouTubeProfile | null; youtubeVideos: YouTubeVideo[];
  snapchatProfile: SnapchatProfile | null; snapchatSnaps: SnapchatSnap[];
  snapchatStories: SnapchatStory[]; snapchatSpotlights: SnapchatSpotlight[];
}) {
  const [tab, setTab] = useState<'instagram' | 'tiktok' | 'youtube' | 'snapchat'>('instagram');
  const [brandCollabs, setBrandCollabs] = useState<BrandCollab[]>([]);
  const [tooltipBrand, setTooltipBrand] = useState<string | null>(null);

  useEffect(() => {
    setTab('instagram');
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, inf.id]);

  useEffect(() => {
    setBrandCollabs([]);
    supabase
      .from('collabs')
      .select('brand, type, date')
      .contains('influencer_ids', [inf.id])
      .not('brand', 'is', null)
      .order('date', { ascending: false })
      .limit(100)
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        const map = new Map<string, BrandCollab>();
        for (const c of data) {
          const key = c.brand.trim().toLowerCase();
          if (map.has(key)) {
            const entry = map.get(key)!;
            entry.count++;
            if (c.type && !entry.types.includes(c.type)) entry.types.push(c.type);
          } else {
            map.set(key, {
              brand: c.brand.trim(),
              count: 1,
              latestDate: c.date,
              types: c.type ? [c.type] : [],
            });
          }
        }
        setBrandCollabs(Array.from(map.values()).slice(0, 12));
      });
  }, [inf.id]);

  const hasTiktok = !!tiktokProfile;
  const hasYoutube = !!youtubeProfile;
  const hasSnapchat = !!snapchatProfile;

  const stats = tab === 'tiktok' && tiktokProfile
    ? [
        { label: 'Abonnés', value: formatNumber(tiktokProfile.followers_count), icon: Users, color: 'text-blue-400' },
        { label: 'Engagement', value: tiktokProfile.engagement_rate ? `${tiktokProfile.engagement_rate.toFixed(1)}%` : 'N/A', icon: TrendingUp, color: 'text-emerald-400' },
        { label: 'Moy. vues', value: formatNumber(tiktokProfile.avg_views), icon: Eye, color: 'text-amber-400' },
      ]
    : tab === 'youtube' && youtubeProfile
    ? [
        { label: 'Abonnés', value: formatNumber(youtubeProfile.subscribers_count), icon: Users, color: 'text-blue-400' },
        { label: 'Engagement', value: youtubeProfile.engagement_rate ? `${youtubeProfile.engagement_rate.toFixed(1)}%` : 'N/A', icon: TrendingUp, color: 'text-emerald-400' },
        { label: 'Moy. vues', value: formatNumber(youtubeProfile.avg_views), icon: Eye, color: 'text-amber-400' },
      ]
    : tab === 'snapchat' && snapchatProfile
    ? (() => {
        const avgViews = snapchatSpotlights.length > 0
          ? Math.round(snapchatSpotlights.reduce((s, sp) => s + (sp.views || 0), 0) / snapchatSpotlights.length)
          : 0;
        return [
          { label: 'Abonnés', value: formatNumber(snapchatProfile.subscriber_count), icon: Users, color: 'text-yellow-400' },
          ...(avgViews > 0 ? [{ label: 'Vues moy.', value: formatNumber(avgViews), icon: Eye, color: 'text-yellow-400' }] : []),
          { label: 'Spotlights', value: String(snapchatSpotlights.length), icon: Play, color: 'text-yellow-400' },
        ];
      })()
    : [
        { label: 'Abonnés', value: formatNumber(inf.followers_count), icon: Users, color: 'text-blue-400' },
        { label: 'Engagement', value: `${inf.engagement_rate?.toFixed(1)}%`, icon: TrendingUp, color: 'text-emerald-400' },
        ...(inf.avg_views != null ? [{ label: 'Moy. vues', value: formatNumber(inf.avg_views), icon: Eye, color: 'text-amber-400' }] : []),
      ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-md" onClick={onClose}>
      <div
        className="relative w-full sm:max-w-lg bg-[#141414] rounded-t-3xl sm:rounded-3xl border border-white/[0.08] overflow-hidden max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-white" />
        </button>

        {/* Profile header */}
        <div className="px-5 pt-4 pb-4 flex items-center gap-4 flex-shrink-0">
          <div className="relative">
            <img
              src={proxyImg(inf.profile_image_url) || `https://ui-avatars.com/api/?name=${inf.username}&background=1e293b&color=94a3b8&size=80`}
              alt={inf.username}
              className="w-16 h-16 rounded-2xl object-cover"
            />
            {inf.is_exclusive && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center border-2 border-[#141414]">
                <BadgeCheck className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white">@{inf.username}</h3>
            {inf.display_name && <p className="text-white/50 text-sm">{inf.display_name}</p>}
            {hasTiktok && tab === 'tiktok' && (
              <p className="text-[#00F2EA] text-xs mt-0.5">@{tiktokProfile!.tiktok_username}</p>
            )}
            {hasYoutube && tab === 'youtube' && (
              <p className="text-[#FF0000] text-xs mt-0.5">{youtubeProfile!.channel_name || youtubeProfile!.youtube_handle}</p>
            )}
            {hasSnapchat && tab === 'snapchat' && (
              <p className="text-[#FFFC00] text-xs mt-0.5">@{snapchatProfile!.username}</p>
            )}
          </div>
        </div>

        {/* Platform tabs */}
        {(hasTiktok || hasYoutube || hasSnapchat) && (
          <div className="flex gap-2 px-5 mb-4 flex-shrink-0 flex-wrap">
            <button
              onClick={() => setTab('instagram')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                tab === 'instagram'
                  ? 'bg-gradient-to-r from-[#833AB4] to-[#E1306C] text-white shadow'
                  : 'bg-white/[0.06] text-white/40 hover:text-white/70'
              }`}
            >
              <Instagram className="w-3.5 h-3.5" />
              Instagram
            </button>
            {hasTiktok && (
              <button
                onClick={() => setTab('tiktok')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  tab === 'tiktok'
                    ? 'bg-white text-black shadow'
                    : 'bg-white/[0.06] text-white/40 hover:text-white/70'
                }`}
              >
                <TikTokIcon className="w-3.5 h-3.5" />
                TikTok
              </button>
            )}
            {hasYoutube && (
              <button
                onClick={() => setTab('youtube')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  tab === 'youtube'
                    ? 'bg-[#FF0000] text-white shadow'
                    : 'bg-white/[0.06] text-white/40 hover:text-white/70'
                }`}
              >
                <YouTubeIcon className="w-3.5 h-3.5" />
                YouTube
              </button>
            )}
            {hasSnapchat && (
              <button
                onClick={() => setTab('snapchat')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  tab === 'snapchat'
                    ? 'bg-[#FFFC00] text-black shadow'
                    : 'bg-white/[0.06] text-white/40 hover:text-white/70'
                }`}
              >
                <SnapchatIcon className="w-3.5 h-3.5" />
                Snapchat
              </button>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 px-5 mb-4 flex-shrink-0">
          {stats.map(stat => (
            <div key={stat.label} className="bg-white/[0.05] rounded-2xl p-3 text-center">
              <stat.icon className={`w-4 h-4 mx-auto mb-1 ${stat.color}`} />
              <p className="text-base font-bold text-white">{stat.value}</p>
              <p className="text-[10px] text-white/40">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Bio */}
        {inf.bio && tab === 'instagram' && (
          <div className="px-5 mb-4 flex-shrink-0">
            <p className="text-sm text-white/50 leading-relaxed line-clamp-3">{inf.bio}</p>
          </div>
        )}

        {/* Déjà collaboré avec */}
        {brandCollabs.length > 0 && (() => {
          const shouldScroll = brandCollabs.length > 4;
          const displayItems = shouldScroll ? [...brandCollabs, ...brandCollabs] : brandCollabs;
          return (
          <div className="mb-4 flex-shrink-0">
            <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest mb-2.5 px-5">Déjà collaboré avec</p>
            <div className={shouldScroll ? 'relative overflow-hidden' : 'px-5'}>
              {shouldScroll && (
                <>
                  <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#141414] to-transparent z-10 pointer-events-none" />
                  <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#141414] to-transparent z-10 pointer-events-none" />
                </>
              )}
              <div
                className="flex gap-2"
                style={{
                  ...(shouldScroll ? {
                    animation: `brandScroll ${brandCollabs.length * 3}s linear infinite`,
                    width: 'max-content',
                    paddingLeft: '20px',
                  } : { flexWrap: 'wrap' }),
                }}
                onMouseEnter={shouldScroll ? e => (e.currentTarget.style.animationPlayState = 'paused') : undefined}
                onMouseLeave={shouldScroll ? e => (e.currentTarget.style.animationPlayState = 'running') : undefined}
              >
                {displayItems.map((bc, i) => (
                  <div
                    key={`${bc.brand}-${i}`}
                    className="relative flex-shrink-0"
                    onMouseEnter={() => setTooltipBrand(`${bc.brand}-${i}`)}
                    onMouseLeave={() => setTooltipBrand(null)}
                  >
                    <div className="flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] rounded-xl px-2.5 py-1.5 cursor-default transition-colors">
                      <BrandLogo brand={bc.brand} size={28} />
                      <span className="text-xs font-medium text-white/80 whitespace-nowrap">{bc.brand}</span>
                      {bc.count > 1 && (
                        <span className="text-[10px] font-bold text-white/40 bg-white/[0.08] rounded-full px-1.5 py-0.5 leading-none">
                          x{bc.count}
                        </span>
                      )}
                    </div>
                    {tooltipBrand === `${bc.brand}-${i}` && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 bg-[#1e1e1e] border border-white/[0.12] rounded-xl px-3 py-2 shadow-xl min-w-[140px] text-center pointer-events-none">
                        <p className="text-xs font-semibold text-white mb-1">{bc.brand}</p>
                        {bc.types.length > 0 && (
                          <p className="text-[11px] text-white/50">{bc.types.map(getCollabTypeLabel).join(', ')}</p>
                        )}
                        {bc.latestDate && (
                          <p className="text-[10px] text-white/30 mt-0.5">
                            {new Date(bc.latestDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                          </p>
                        )}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-[#1e1e1e]" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          );
        })()}

        {/* Content grid */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {tab === 'instagram' && (
            <>
              <p className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-3">Meilleurs posts</p>
              {loadingPosts ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                </div>
              ) : posts.length === 0 ? (
                <p className="text-center text-white/25 text-sm py-6">Aucun post disponible</p>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {posts.map(post => (
                    <a
                      key={post.id}
                      href={post.post_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative aspect-square rounded-xl overflow-hidden group"
                      onClick={e => !post.post_url && e.preventDefault()}
                    >
                      {post.image_url ? (
                        <img src={proxyImg(post.image_url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full bg-white/[0.05] flex items-center justify-center">
                          <Instagram className="w-5 h-5 text-white/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <span className="flex items-center gap-1 text-xs font-semibold text-white">
                          <Heart className="w-3 h-3 fill-white" /> {formatNumber(post.likes_count || 0)}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'tiktok' && (
            <>
              <p className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-3">Dernières vidéos TikTok</p>
              {loadingPosts ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                </div>
              ) : tiktokVideos.length === 0 ? (
                <p className="text-center text-white/25 text-sm py-6">Aucune vidéo disponible</p>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {tiktokVideos.map(video => (
                    <a
                      key={video.id}
                      href={video.video_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative rounded-xl overflow-hidden group"
                      onClick={e => !video.video_url && e.preventDefault()}
                      style={{ aspectRatio: '9/16' }}
                    >
                      {video.thumbnail_url ? (
                        <img src={proxyImg(video.thumbnail_url)} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full bg-white/[0.05] flex items-center justify-center">
                          <TikTokIcon className="w-6 h-6 text-white/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                        <Play className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5">
                        <div className="flex items-center justify-between text-white text-[10px] font-semibold">
                          <span className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{formatNumber(video.views_count)}</span>
                          <span className="flex items-center gap-0.5"><Heart className="w-2.5 h-2.5" />{formatNumber(video.likes_count)}</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'snapchat' && (
            <>
              {snapchatProfile?.bio && (
                <p className="text-sm text-white/50 leading-relaxed mb-3 line-clamp-3">{snapchatProfile.bio}</p>
              )}
              {snapchatProfile?.snapcode_url && (
                <div className="flex justify-center mb-4">
                  <img
                    src={snapchatProfile.snapcode_url}
                    alt="Snapcode"
                    className="w-24 h-24 rounded-2xl"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
              {loadingPosts ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[#FFFC00]/40 border-t-[#FFFC00] rounded-full animate-spin" />
                </div>
              ) : snapchatStories.length === 0 && snapchatSpotlights.length === 0 ? (
                <p className="text-center text-white/25 text-sm py-6">Aucun contenu disponible</p>
              ) : (
                <div className="space-y-5">
                  {/* Stories */}
                  {snapchatStories.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-3">Stories récentes</p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {snapchatStories.map(story => (
                          <div key={story.id} className="relative rounded-xl overflow-hidden group" style={{ aspectRatio: '9/16' }}>
                            {story.thumbnail_url ? (
                              <img src={story.thumbnail_url} alt={story.story_title || ''} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full bg-[#FFFC00]/10 flex items-center justify-center">
                                <SnapchatIcon className="w-6 h-6 text-[#FFFC00]/40" />
                              </div>
                            )}
                            {story.story_title && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1">
                                <p className="text-white text-[9px] font-semibold truncate">{story.story_title}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Spotlights */}
                  {snapchatSpotlights.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-3">Spotlights</p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {snapchatSpotlights.map(sp => (
                          <div key={sp.id} className="relative rounded-xl overflow-hidden group" style={{ aspectRatio: '9/16' }}>
                            {sp.thumbnail_url ? (
                              <img src={sp.thumbnail_url} alt={sp.title || ''} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full bg-[#FFFC00]/10 flex items-center justify-center">
                                <SnapchatIcon className="w-6 h-6 text-[#FFFC00]/40" />
                              </div>
                            )}
                            {sp.views > 0 && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1.5">
                                <div className="flex items-center gap-0.5 text-white text-[9px] font-semibold">
                                  <Eye className="w-2.5 h-2.5" />{formatNumber(sp.views)}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {tab === 'youtube' && (
            <>
              <p className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-3">Dernières vidéos YouTube</p>
              {loadingPosts ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                </div>
              ) : youtubeVideos.length === 0 ? (
                <p className="text-center text-white/25 text-sm py-6">Aucune vidéo disponible</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {youtubeVideos.map(video => (
                    <a
                      key={video.id}
                      href={video.video_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative rounded-xl overflow-hidden group"
                      onClick={e => !video.video_url && e.preventDefault()}
                    >
                      <div className="aspect-video relative">
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt={video.title || ''} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <div className="w-full h-full bg-white/[0.05] flex items-center justify-center">
                            <YouTubeIcon className="w-6 h-6 text-[#FF0000]/40" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                          <Play className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5">
                          <div className="flex items-center justify-between text-white text-[10px] font-semibold">
                            <span className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{formatNumber(video.view_count)}</span>
                            <span className="flex items-center gap-0.5"><Heart className="w-2.5 h-2.5" />{formatNumber(video.likes_count)}</span>
                          </div>
                        </div>
                      </div>
                      {video.title && (
                        <p className="px-1 pt-1 pb-0.5 text-[10px] text-white/50 line-clamp-1 leading-tight">{video.title}</p>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        <div className="px-5 pb-5 pt-3 border-t border-white/[0.06] flex-shrink-0">
          <button
            onClick={onLike}
            className={`w-full py-3.5 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2.5 transition-all ${
              liked
                ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                : 'bg-white text-black hover:bg-white/90 active:scale-[0.98]'
            }`}
          >
            <Heart className={`w-4 h-4 ${liked ? 'fill-rose-400' : ''}`} />
            {liked ? 'Profil ajouté à vos favoris' : 'Ce profil m\'intéresse'}
          </button>
          <div className="flex gap-2 mt-2">
            {inf.username && (
              <a
                href={`https://instagram.com/${inf.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 text-white/50 hover:text-white/80 transition-colors"
              >
                <Instagram className="w-4 h-4" />
                Instagram
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {hasTiktok && tiktokProfile && (
              <a
                href={`https://tiktok.com/@${tiktokProfile.tiktok_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 text-white/50 hover:text-white/80 transition-colors"
              >
                <TikTokIcon className="w-4 h-4" />
                TikTok
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {hasYoutube && youtubeProfile && (
              <a
                href={`https://youtube.com/${youtubeProfile.youtube_handle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 text-white/50 hover:text-white/80 transition-colors"
              >
                <YouTubeIcon className="w-4 h-4 text-[#FF0000]" />
                YouTube
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {hasSnapchat && snapchatProfile && (
              <a
                href={`https://snapchat.com/add/${snapchatProfile.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 text-white/50 hover:text-white/80 transition-colors"
              >
                <SnapchatIcon className="w-4 h-4 text-[#FFFC00]" />
                Snapchat
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
